import React, { useEffect, useRef, useState } from 'react';
import { lightweightChartEngine, createReplayController, ReplayController } from '@smartcharts/chart-web';
import { TQuote } from '@smartcharts/core';

type TDemoProps = {
    symbol?: string;
    granularity?: number; // 0 for tick line series, >0 for candlesticks (seconds)
    points?: number; // initial history points
};

const generateHistory = (count: number, granularity: number, start: Date): TQuote[] => {
    const quotes: TQuote[] = [];
    let price = 100 + Math.random() * 5;
    for (let i = 0; i < count; i++) {
        const dt = new Date(start.getTime() + i * 1000 * (granularity === 0 ? 1 : granularity));
        const drift = (Math.random() - 0.5) * 0.5;
        if (granularity === 0) {
            price = Math.max(1, price + drift);
            quotes.push({ Date: dt.toISOString().replace('Z', ''), Close: Number(price.toFixed(4)), DT: dt });
        } else {
            const open = price;
            const high = open + Math.random();
            const low = open - Math.random();
            const close = low + Math.random() * (high - low);
            price = close;
            quotes.push({
                Date: dt.toISOString().replace('Z', ''),
                Open: Number(open.toFixed(4)),
                High: Number(high.toFixed(4)),
                Low: Number(low.toFixed(4)),
                Close: Number(close.toFixed(4)),
                DT: dt,
            });
        }
    }
    return quotes;
};

export const LightweightChartDemo: React.FC<TDemoProps> = ({ symbol = 'RANDOM', granularity = 0, points = 200 }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [replayController, setReplayController] = useState<ReplayController | null>(null);
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode'); // 'replay' to enable replay playback
    const speedParam = params.get('speed');
    const speed = speedParam ? parseFloat(speedParam) : 10;

    useEffect(() => {
        if (!containerRef.current) return;
        lightweightChartEngine.newChart({
            container: containerRef.current,
            symbol,
            granularity,
            chartType: granularity === 0 ? 'line' : 'candlestick',
            theme: 'light',
            isMobile: false,
            isLive: true,
            startWithDataFitMode: true,
        });
        const history = generateHistory(points, granularity, new Date(Date.now() - points * 1000 * (granularity === 0 ? 1 : granularity)));
        if (mode === 'replay') {
            const controller = createReplayController(lightweightChartEngine);
            controller.loadHistorical(history, symbol, granularity);
            controller.play(speed);
            setReplayController(controller);
            return;
        }
        lightweightChartEngine.onTickHistory(history, false);
        const interval = setInterval(() => {
            const last = history[history.length - 1];
            const lastDt = last.DT || new Date(Date.parse(last.Date + 'Z'));
            const nextDt = new Date(lastDt.getTime() + 1000 * (granularity === 0 ? 1 : granularity));
            const base = last.Close;
            if (granularity === 0) {
                const nextClose = Math.max(1, base + (Math.random() - 0.5));
                const tick: TQuote = { Date: nextDt.toISOString().replace('Z', ''), Close: Number(nextClose.toFixed(4)), DT: nextDt };
                history.push(tick);
                lightweightChartEngine.onNewTick(tick);
            } else {
                const open = base;
                const high = open + Math.random();
                const low = open - Math.random();
                const close = low + Math.random() * (high - low);
                const candle: TQuote = {
                    Date: nextDt.toISOString().replace('Z', ''),
                    Open: Number(open.toFixed(4)),
                    High: Number(high.toFixed(4)),
                    Low: Number(low.toFixed(4)),
                    Close: Number(close.toFixed(4)),
                    DT: nextDt,
                };
                history.push(candle);
                lightweightChartEngine.onNewCandle(candle);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [symbol, granularity, points, mode, speed]);

    const state = replayController?.getState();

    return (
        <div style={{ position: 'fixed', inset: 0, background: '#fff' }}>
            {mode === 'replay' && state && (
                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10, fontSize: 12, padding: '4px 8px', background: 'rgba(255,255,255,0.9)', borderRadius: 4 }}>
                    <strong>Replay</strong> epoch: {state.currentEpoch} progress: {state.progress.toFixed(1)}% speed: {state.speed} t/s
                </div>
            )}
            <div style={{ width: '100%', height: '100%', overflow: 'hidden' }} ref={containerRef} />
        </div>
    );
};
